import React, { useState } from 'react';
import {
  Card,
  Form,
  Button,
  Alert,
  Container,
  Row,
  Col,
  Spinner,
  InputGroup
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import SettingsPageHeader from '../components/SettingsPageHeader';
import { authAPI } from '../utils/api';
import { validatePassword, passwordErrorMessage } from '../utils/passwordValidation';

const AccountSettings: React.FC = () => {
  const { t } = useTranslation('auth', { keyPrefix: 'accountSettings' });
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passError, setPassError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  const [showPass, setShowPass] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'new_password' && passError) {
      const validation = validatePassword(value);
      setPassError(validation.valid ? '' : (validation.error || ''));
    }
    
    if (name === 'new_password_confirmation' && confirmError) {
      setConfirmError(value === formData.new_password ? '' : t('passwordsDoNotMatch'));
    }

    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const validation = validatePassword(formData.new_password);
    if (!validation.valid) {
      setPassError(validation.error || '');
      return;
    }

    if (formData.new_password !== formData.new_password_confirmation) {
      setConfirmError(t('passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.changePassword(formData);
      setSuccess(response.message || t('passwordUpdated'));
      setFormData({
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.message || t('passwordChangeError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Container fluid className="p-4">
        <SettingsPageHeader
          title={t('title')}
          settingsPath="/settings"
        />

        <Row className="justify-content-center mt-4">
          <Col md={8} lg={6}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-bottom py-3">
                <h5 className="mb-0">{t('changePassword')}</h5>
              </Card.Header>
              <Card.Body className="p-4">
                {error && <Alert variant="danger" className="mb-4">{error}</Alert>}
                {success && <Alert variant="success" className="mb-4">{success}</Alert>}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('currentPassword')}</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPass.current ? 'text' : 'password'}
                        name="current_password"
                        value={formData.current_password}
                        onChange={handleInputChange}
                        required
                        placeholder={t('currentPasswordPlaceholder')}
                      />
                      <Button 
                        variant="outline-secondary"
                        onClick={() => setShowPass(prev => ({ ...prev, current: !prev.current }))}
                      >
                        {showPass.current ? t('hide') : t('show')}
                      </Button>
                    </InputGroup>
                  </Form.Group>

                  <hr className="my-4" />

                  <Form.Group className="mb-3">
                    <Form.Label>{t('newPassword')}</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPass.new ? 'text' : 'password'}
                        name="new_password"
                        value={formData.new_password}
                        onChange={handleInputChange}
                        isInvalid={!!passError}
                        required
                        placeholder={t('newPasswordPlaceholder')}
                      />
                      <Button 
                        variant="outline-secondary"
                        onClick={() => setShowPass(prev => ({ ...prev, new: !prev.new }))}
                      >
                        {showPass.new ? t('hide') : t('show')}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {passError}
                      </Form.Control.Feedback>
                    </InputGroup>
                    {!passError && (
                      <Form.Text className="text-muted">
                        {passwordErrorMessage}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>{t('confirmNewPassword')}</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPass.confirm ? 'text' : 'password'}
                        name="new_password_confirmation"
                        value={formData.new_password_confirmation}
                        onChange={handleInputChange}
                        isInvalid={!!confirmError}
                        required
                        placeholder={t('confirmPasswordPlaceholder')}
                      />
                      <Button 
                        variant="outline-secondary"
                        onClick={() => setShowPass(prev => ({ ...prev, confirm: !prev.confirm }))}
                      >
                        {showPass.confirm ? t('hide') : t('show')}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {confirmError}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={loading || !!passError || !!confirmError}
                      className="py-2 fw-bold"
                    >
                      {loading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          {t('updating')}
                        </>
                      ) : (
                        t('updatePassword')
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default AccountSettings;
